import Typography from "antd/lib/typography";
import clsx from "clsx";
import { formatDistanceToNow, Locale } from "date-fns";
import { enGB, es, fr } from "date-fns/locale";
import { isString } from "lodash";
import { ComponentPropsWithoutRef, RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useActiveClassroom } from "@sol/classrooms";
import {
    getResourceUuid,
    IClassroomItem,
    IClassroomUser,
    IFramework,
    IFrameworkItem,
    IFrameworkSkill,
    IUser,
    IValidationResource,
    useFrameworkListQuery,
} from "@sol/sdk";
import { PaperPlane } from "@sol/uikit/core/icons/PaperPlane";
import { Badge } from "@sol/uikit/data-display/Badge/Badge";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";

import { SkillsMetrics } from "@/entities/skill";
import { UserStatusLabel } from "@/entities/user";
import { FrameworkSkillsValidations } from "@/features/framework-skills-validations";
import useLocale from "@/shared/hooks/useLocale";

import { LearnerResultsDownloadButton } from "../../../components/shared/LearnerResultsDownloadButton";
import useActiveClassroomFrameworks from "../../../hooks/useActiveClassroomFrameworks";

const localeToDateFNSLocale: { [key: string]: Locale } = {
    en: enGB,
    fr,
    es,
};

type Props = {
    lastLearnerClassroom?: IClassroomItem;

    learner: IClassroomUser | IUser;
    validations: IValidationResource[];
    isLoading: boolean;
    focusRef: RefObject<HTMLHeadingElement>;
} & ComponentPropsWithoutRef<"div">;

export const LearnerDetails = ({
    lastLearnerClassroom,
    learner,
    validations,
    isLoading,
    focusRef,
    className,
    ...rest
}: Props) => {
    const { name, surname, email, connectedAt, status } = learner;
    const [locale] = useLocale();
    const [t] = useTranslation();
    const { activeClassroom } = useActiveClassroom();

    //queryOptim:: We chose a time of 24 hours before the data is considered stale so that users
    //who keep their browser tabs open without refreshing the page will at least get their data updated every 24 hours.
    const { data: learnerFrameworks, isInitialLoading: isLoadingLearnerFrameworks } = useFrameworkListQuery(
        {
            filters: {
                learners: learner && getResourceUuid(learner),
            },
            pagination: false,
        },
        {
            enabled: !activeClassroom,
            staleTime: 1000 * 60 * 60 * 24,
        },
    );

    const frameworks: (IFrameworkItem | IFramework)[] = activeClassroom
        ? useActiveClassroomFrameworks()
        : learnerFrameworks
          ? learnerFrameworks["hydra:member"]
          : [];

    const skills = useMemo(() => {
        const acc: string[] = [];
        frameworks.forEach(framework => {
            framework.skills.forEach((skill: IFrameworkSkill | string) => {
                const skillId = isString(skill) ? skill : skill["@id"];
                if (!acc.includes(skillId)) {
                    acc.push(skillId);
                }
            });
        });

        return acc;
    }, [frameworks]);

    let children =
        isLoading || isLoadingLearnerFrameworks ? (
            <div className="m-0 flex size-full min-h-0 flex-1 items-center justify-center">
                <Skeleton.Node className="size-full!" active />
            </div>
        ) : (
            <div className="m-0 flex size-full min-h-0 flex-1 items-center justify-center">
                <Typography.Text className="text-ant-h2 font-ant-strong">
                    {t("page.classrooms.home.noSkill")}
                </Typography.Text>
            </div>
        );

    if (validations && validations.length > 0) {
        children = (
            <>
                <div className="mb-5">
                    <SkillsMetrics skillsCount={skills.length} validations={validations} />
                </div>
                <div className="overflow-auto">
                    <ul className="relative m-0 flex min-h-0 flex-1 flex-col gap-5 p-0">
                        {frameworks.map(framework => (
                            <FrameworkSkillsValidations
                                key={framework["@id"]}
                                framework={framework}
                                validations={validations}
                                learner={learner}
                                isInCard
                            />
                        ))}
                    </ul>
                </div>
            </>
        );
    }

    const classroom = activeClassroom ? activeClassroom : lastLearnerClassroom;

    return (
        <div {...rest} className={clsx("flex h-full flex-col py-5 pr-5", className)}>
            <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center">
                    <Typography.Title level={2} className="my-0! text-ant-h3" tabIndex={-1} ref={focusRef}>
                        {name} <Typography.Text className="font-ant-strong uppercase">{surname}</Typography.Text>
                    </Typography.Title>
                    <Typography.Text className="ml-6 text-color-ant-secondary">{email}</Typography.Text>
                    <div className="ml-5 flex items-center">
                        {connectedAt ? (
                            <Typography.Text className="ml-2 font-ant-strong text-color-ant-secondary">
                                <Badge color="green" className="mr-2" />
                                {formatDistanceToNow(new Date(connectedAt), {
                                    locale: localeToDateFNSLocale[locale as string],
                                })}
                            </Typography.Text>
                        ) : (
                            <div className="flex items-center gap-2">
                                <UserStatusLabel status={status} className="lowercase" />
                                <PaperPlane size={14} className="text-primary" />
                            </div>
                        )}
                    </div>
                </div>
                {classroom && (
                    <LearnerResultsDownloadButton classroom={classroom} learner={learner}>
                        {activeClassroom
                            ? t("page.classrooms.home.learners.learnerTrainingReport.default")
                            : t("page.classrooms.home.learners.learnerTrainingReport.lastClassroom")}
                    </LearnerResultsDownloadButton>
                )}
            </div>

            {children}
        </div>
    );
};
